import { useState, useMemo, useCallback, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { useTranslate } from "src/locales";
import {
  requestService,
  ServiceCategoryDto,
} from "src/api/request/requestService";

export interface UseCategoryManagementStateProps {
  huv: boolean;
  setServices: Dispatch<SetStateAction<any[]>>;
  setLoadingServices: Dispatch<SetStateAction<boolean>>;
  open: boolean; // to trigger fetches
  plpId?: number | null;
}

export function useCategoryManagementState({
  huv,
  setServices,
  setLoadingServices,
  open,
  plpId
}: UseCategoryManagementStateProps) {
  const { t } = useTranslate();
  
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>("sık-kullanılanlar");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [serviceCategories, setServiceCategories] = useState<ServiceCategoryDto[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<"main" | "subcategory" | "history" | "frequent">("frequent");
  
  const [sidebarSearchText, setSidebarSearchText] = useState("");
  const [contentSearchText, setContentSearchText] = useState("");

  const selectedCategoryIdRef = useRef<number | null>(null);

  // --- Helpers ---

  const flattenServiceCategories = useCallback((categories: ServiceCategoryDto[], parentId: string | null = "tümü", depth: number = 0): any[] => {
    const result: any[] = [];

    categories.forEach((category) => {
      const categoryId = `category-${category.id}`;
      const hasChildren = category.child_categories && category.child_categories.length > 0;

      result.push({
        id: categoryId,
        name: category.name,
        icon: hasChildren ? "arrow" : "plus",
        type: depth === 0 ? "sub" : "child",
        parent: parentId,
        categoryId: category.id,
        depth: depth,
      });

      if (hasChildren) {
        const childResults = flattenServiceCategories(category.child_categories, categoryId, depth + 1);
        result.push(...childResults);
      }
    });

    return result;
  }, []);

  // --- Derived Data ---

  const categories = useMemo(() => {
    const baseCategories = [
      { id: "geçmiş", name: t("patient-request-service.categories.history"), icon: null, type: "main" },
      { id: "sık-kullanılanlar", name: t("patient-request-service.categories.frequent"), icon: null, type: "main" },
      { id: "tümü", name: t("patient-request-service.categories.all"), icon: null, type: "main" },
    ];

    const serviceCats = flattenServiceCategories(serviceCategories);

    return [...baseCategories, ...serviceCats];
  }, [serviceCategories, flattenServiceCategories, t]);

  const filteredCategoriesList = useMemo(() => {
    if (!sidebarSearchText) return categories;
    const lower = sidebarSearchText.toLocaleLowerCase("tr-TR");
    
    // Find all categories that match the search text
    const matches = categories.filter(cat => cat.name.toLocaleLowerCase("tr-TR").includes(lower));
    const keepIds = new Set<string>();
    
    // For each match, add it and all its ancestors to the set
    for (const match of matches) {
        keepIds.add(match.id);
        let current = match;
        // Traverse up to find ancestors
        while(current.parent && current.parent !== "root" && current.parent !== "tümü") {
            const parent = categories.find(c => c.id === current.parent);
            if (parent) {
                keepIds.add(parent.id);
                current = parent;
            } else {
                break;
            }
        }
    }
    
    return categories.filter(cat => keepIds.has(cat.id));
  }, [categories, sidebarSearchText]);

  const mainCategories = useMemo(() => {
    return (sidebarSearchText ? filteredCategoriesList : categories).filter(cat => cat.type === "main");
  }, [sidebarSearchText, filteredCategoriesList, categories]);

  const rootCategories = useMemo(() => {
    return (sidebarSearchText ? filteredCategoriesList : categories).filter(cat => {
      if (selectedMainCategory === "tümü") {
        return cat.type === "sub" && (cat.parent === "tümü" || (sidebarSearchText && cat.parent !== "root"));
      } else if (selectedMainCategory === "geçmiş") {
        return cat.type === "sub" && cat.parent === "tümü";
      } else if (selectedMainCategory === "sık-kullanılanlar") {
        return cat.type === "sub" && cat.parent === "tümü";
      }
      return cat.type === "sub" && cat.parent === selectedMainCategory;
    });
  }, [sidebarSearchText, filteredCategoriesList, categories, selectedMainCategory]);

  const visibleCategories = useMemo(() => {
    const result: any[] = [];
    const sourceList = sidebarSearchText ? filteredCategoriesList : categories;
    
    const addCategoryAndChildren = (parentId: string, currentDepth: number = 0) => {
      const children = sourceList.filter(cat => cat.parent === parentId);
      
      children.forEach(child => {
        result.push(child);
        if (expandedCategories.includes(child.id)) {
          addCategoryAndChildren(child.id, currentDepth + 1);
        }
      });
    };
    
    rootCategories.forEach(root => {
      result.push(root);
      if (expandedCategories.includes(root.id)) {
        addCategoryAndChildren(root.id, 0);
      }
    });
    
    return result;
  }, [categories, rootCategories, expandedCategories, sidebarSearchText, filteredCategoriesList]);

  // --- Handlers ---

  const handleMainCategorySelect = async (categoryId: string) => {
    setSelectedMainCategory(categoryId);
    setSelectedSubCategory("");
    setExpandedCategories([]);
    setServices([]);

    switch (categoryId) {
      case "geçmiş":
        setCurrentView("history");
        break;
      case "sık-kullanılanlar":
        setCurrentView("frequent");
        try {
          setLoadingServices(true);
          const response = await requestService.getAddServiceFavorites(huv, plpId || undefined);
          if (response && response.data && Array.isArray(response.data)) {
            setServices(response.data);
          }
        } catch (error) {
          console.error("Error fetching favorite services:", error);
        } finally {
          setLoadingServices(false);
        }
        break;
      case "tümü":
        setCurrentView("main");
        try {
          const response = await requestService.getServiceCategoriesWithNames();
          if (response && response.data && Array.isArray(response.data)) {
            setServiceCategories(response.data);
          }
        } catch (error) {
          console.error("Error fetching service categories:", error);
        }
        break;
      default:
        setCurrentView("main");
    }
  };

  const handleSubCategorySelect = async (categoryId: string) => {
    setSelectedSubCategory(categoryId);
    setCurrentView("subcategory");
    setServices([]);

    const selectedCategory = categories.find(cat => cat.id === categoryId);
    
    if (selectedCategory && selectedCategory.icon !== "arrow" && selectedCategory.categoryId) {
      selectedCategoryIdRef.current = selectedCategory.categoryId;
    } else {
      selectedCategoryIdRef.current = null;
    }
    
    if (selectedCategory && selectedCategory.icon !== "arrow" && selectedCategory.categoryId) {
      try {
        setLoadingServices(true);
        const response = await requestService.getAddServices(selectedCategory.categoryId, huv, plpId || undefined);
        if (response && response.data && Array.isArray(response.data)) {
          setServices(response.data);
        }
      } catch (error) {
        console.error("Error fetching services for category:", error);
      } finally {
        setLoadingServices(false);
      }
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
    } else {
      setExpandedCategories([...expandedCategories, categoryId]);
    }
  };

  // --- Effects ---

  // Search effect
  useEffect(() => {
    if (sidebarSearchText) {
      setSelectedMainCategory("tümü");
      
      const lower = sidebarSearchText.toLocaleLowerCase("tr-TR");
      const matches = categories.filter(cat => cat.name.toLocaleLowerCase("tr-TR").includes(lower));
      const ancestorsToExpand = new Set<string>();

      for (const match of matches) {
          let current = match;
          while(current.parent && current.parent !== "root" && current.parent !== "tümü") {
              const parent = categories.find(c => c.id === current.parent);
              if (parent) {
                  ancestorsToExpand.add(parent.id);
                  current = parent;
              } else {
                  break;
              }
          }
      }
      
      if (ancestorsToExpand.size > 0) {
        setExpandedCategories((prev) => {
            const next = new Set([...prev, ...Array.from(ancestorsToExpand)]);
            return Array.from(next);
        });
      }
    }
  }, [sidebarSearchText, categories]);

  // HUV change effect
  useEffect(() => {
    if (!open) return;
    
    if (currentView === "frequent") {
      (async () => {
        try {
          setLoadingServices(true);
          setServices([]);
          const response = await requestService.getAddServiceFavorites(huv, plpId || undefined);
          if (response && response.data && Array.isArray(response.data)) {
            setServices(response.data);
          }
        } catch (error) {
          console.error("Error refetching favorite services on HUV change:", error);
        } finally {
          setLoadingServices(false);
        }
      })();
      return;
    }
    
    if (currentView !== "subcategory" || !selectedCategoryIdRef.current) return;
    
    (async () => {
      try {
        setLoadingServices(true);
        setServices([]);
        const response = await requestService.getAddServices(selectedCategoryIdRef.current!, huv, plpId || undefined);
        if (response && response.data && Array.isArray(response.data)) {
          setServices(response.data);
        }
      } catch (error) {
        console.error("Error refetching services on HUV/PLP change:", error);
      } finally {
        setLoadingServices(false);
      }
    })();
  }, [huv, plpId, currentView, open, setServices, setLoadingServices]);

  // Initial Data Fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const serviceCategoriesResponse = await requestService.getServiceCategoriesWithNames();
        if (serviceCategoriesResponse && (serviceCategoriesResponse.data || Array.isArray(serviceCategoriesResponse))) {
          setServiceCategories(serviceCategoriesResponse.data || serviceCategoriesResponse);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    if (open) {
      fetchInitialData();
    }
  }, [open]);

  return {
    // State
    selectedMainCategory, setSelectedMainCategory,
    selectedSubCategory, setSelectedSubCategory,
    serviceCategories, setServiceCategories,
    expandedCategories, setExpandedCategories,
    currentView, setCurrentView,
    sidebarSearchText, setSidebarSearchText,
    contentSearchText, setContentSearchText,
    
    // Derived
    categories,
    visibleCategories,
    mainCategories,
    // Handlers
    handleMainCategorySelect,
    handleSubCategorySelect,
    handleCategoryToggle,
  };
}
